import { useEffect, useState } from "react";
import styles from "../EmployeeAttributes.module.css";
import TextArea from "uicomponents/TextArea";
import RadioInput from "uicomponents/RadioInput";
import SelectInput from "uicomponents/SelectInput";
import DateInput from "uicomponents/DateInput";
import TextInput from "uicomponents/TextInput";
import { useFormikContext, FieldArray } from "formik";
import { EmployeeAttributesFormType } from "../../../utils/formType";
import {
  travelFrequencyOptions,
  percentageOptions,
  contractorNumberOption,
  countryOptions,
} from "../../../utils/selectInputOptions";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import EditCard from "uicomponents/EditCard";
import FooterButton from "uicomponents/FooterButton";
import { EmployeeListAddedPreviously } from "types";
import MultiSelectInput from "uicomponents/MultiSelectInput";

interface IEmployeeAttributesComponentProps {
  formvalues: EmployeeAttributesFormType;
  hasEmployeesData?: EmployeeAttributesFormType;
  employeeCensus: EmployeeListAddedPreviously;
  fieldRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
}
interface memberTravelCountries {
  index: number;
  travelLocation: string[];
}

const EmployeeAttributesComponent = ({
  ...props
}: IEmployeeAttributesComponentProps): JSX.Element => {
  const { hasEmployeesData, formvalues, employeeCensus, fieldRefs } = props;
  const { values, setFieldValue } = useFormikContext();
  const [hasEmployeesBoatsBridges, setHasEmployeesBoatsBridges] = useState([
    "",
  ]);
  const [hasEmployeesPhysicalDisability, setHasEmployeesPhysicalDisability] =
    useState([""]);
  const [hasCountryList, setCountryList] = useState<memberTravelCountries[]>(
    []
  );

  const hasTravelEmployee = (values as EmployeeAttributesFormType)?.travel;
  const hasEmployeesPhysicalLabor = (values as EmployeeAttributesFormType)
    ?.employeesPhysicalLabor;
  const hasContractors = (values as EmployeeAttributesFormType)?.hasContractors;
  const hasEmployeesWorkUnderground = (values as EmployeeAttributesFormType)
    ?.employeesWorkUnderground;
  const hasEmployeesWorkOnLadders = (values as EmployeeAttributesFormType)
    ?.employeesWorkOnLadders;
  const hasDeliveries = (values as EmployeeAttributesFormType)?.deliveries;
  const hasEmployeesWhoUseTools = (values as EmployeeAttributesFormType)
    ?.hardware;
  const hasUnderageOrSeniorEmployees = (values as EmployeeAttributesFormType)
    ?.underageOrSenior;
  const hasEmployeePerformHeavyLifting = (values as EmployeeAttributesFormType)
    ?.employeePerformHeavyLifting;
  const doContractorsHaveWorkComp = (values as EmployeeAttributesFormType)
    ?.contractorsHaveWorkComp;
  const hasBoatsBridgesEmployees = (values as EmployeeAttributesFormType)
    ?.boatsBridges;
  const hasPhysicalDisabilityEmployees = (values as EmployeeAttributesFormType)
    ?.physicalDisability;

  function hasTravelInternationally(index: number) {
    return (
      formvalues?.employeesWhoTravel?.[index]?.travelInternationally === "Yes"
    );
  }

  const labelList = sectionsQuestions["employeesAttributes"].questions;
  const messageList = sectionsQuestions["employeesAttributes"].message;

  useEffect(() => {
    const initialBoatsBridgesValues =
      hasEmployeesData?.employeesWhoUseBoatsBridges || [""];
    setHasEmployeesBoatsBridges(initialBoatsBridgesValues);

    const initialPhysicalDisabilityValues =
      hasEmployeesData?.employeesWhoHavePhysicalDisabilities || [""];
    setHasEmployeesPhysicalDisability(initialPhysicalDisabilityValues);

    if (hasEmployeesData?.employeesWhoTravel) {
      const travelLocations = hasEmployeesData.employeesWhoTravel.map(
        (employee, index) => ({
          index,
          travelLocation: employee.travelLocation.split(", ") || [],
        })
      );
      setCountryList(travelLocations);
    }
  }, [hasEmployeesData]);

  useEffect(() => {
    if (hasTravelEmployee === "No") {
      formvalues.employeesWhoTravel = [
        {
          employeeId: "",
          travelPurpose: "",
          travelInternationally: "",
          travelLocation: "",
          travelFrequency: "",
        },
      ];
    }
  }, [hasTravelEmployee, formvalues]);

  useEffect(() => {
    if (hasEmployeesPhysicalLabor === "No") {
      formvalues.employeesWhoRequirePhysicalLabor = [
        {
          employeeId: "",
          percentPhysicalLabor: "",
        },
      ];
    }
  }, [hasEmployeesPhysicalLabor, formvalues]);

  useEffect(() => {
    if (hasContractors === "No") {
      const updateFieldList = [
        "employmentDuration",
        "numberContractors",
        "percentContractors",
        "contractorWorkType",
        "contractorsHaveWorkComp",
        "contractorsHaveWorkersCompCois",
      ];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasContractors, setFieldValue]);

  useEffect(() => {
    if (hasBoatsBridgesEmployees === "No") {
      setFieldValue("employeesWhoUseBoatsBridges", [""]);
      setHasEmployeesBoatsBridges([""]);
    }
  }, [hasBoatsBridgesEmployees, setFieldValue]);

  useEffect(() => {
    if (hasPhysicalDisabilityEmployees === "No") {
      setFieldValue("employeesWhoHavePhysicalDisabilities", [""]);
      setHasEmployeesPhysicalDisability([""]);
    }
  }, [hasPhysicalDisabilityEmployees, setFieldValue]);

  useEffect(() => {
    if (hasDeliveries === "No") {
      formvalues.employeesWhoMakeDeliveries = [
        {
          employeeId: "",
          deliveryTransportationType: "",
          deliveryDistance: "",
          deliveryFrequency: "",
        },
      ];
    }
  }, [hasDeliveries, formvalues]);

  useEffect(() => {
    if (hasUnderageOrSeniorEmployees === "No") {
      formvalues.underageOrSeniorEmployees = [
        {
          dateOfBirth: "",
          employeeId: "",
          jobTitle: "",
        },
      ];
    }
  }, [hasUnderageOrSeniorEmployees, formvalues]);

  useEffect(() => {
    if (hasEmployeesWhoUseTools === "No") {
      formvalues.employeesWhoUseTools = [
        {
          employeeId: "",
          hardwareNames: "",
        },
      ];
    }
  }, [hasEmployeesWhoUseTools, formvalues]);

  useEffect(() => {
    if (hasEmployeesWorkUnderground === "No") {
      formvalues.employeesWhoWorkUnderground = [
        {
          employeeId: "",
          undergroundDescription: "",
        },
      ];
    }
  }, [hasEmployeesWorkUnderground, formvalues]);

  useEffect(() => {
    if (hasEmployeesWorkOnLadders === "No") {
      formvalues.employeesWhoWorkOnLadders = [
        {
          employeeId: "",
          laddersDescription: "",
        },
      ];
    }
  }, [hasEmployeesWorkOnLadders, formvalues]);

  useEffect(() => {
    if (hasEmployeePerformHeavyLifting === "No") {
      formvalues.employeesWhoPerformHeavyLifting = [
        {
          employeeId: "",
          maximumWeightLifted: "",
        },
      ];
    }
  }, [hasEmployeePerformHeavyLifting, formvalues]);

  useEffect(() => {
    if (doContractorsHaveWorkComp === "No") {
      setFieldValue("contractorsHaveWorkersCompCois", "");
    }
  }, [doContractorsHaveWorkComp, setFieldValue]);

  const employeeAndOwnerOptions = employeeCensus
    ?.filter(
      (member) =>
        member.isCompanyOwner === true ||
        (member?.isCompanyEmployee === true && member?.email)
    )
    .map((member) => {
      const memberName = `${member.firstName} ${member.middleName} ${member.lastName}`;

      return {
        value: member.uuid,
        description: memberName,
      };
    });

  const selectedWhoTravelIds: string[] =
    formvalues.employeesWhoTravel?.map((empVal) => empVal.employeeId || "") ||
    [];

  const selectedWhoUseToolsIds: string[] =
    formvalues.employeesWhoUseTools?.map((empVal) => empVal.employeeId || "") ||
    [];

  const selectedUnderageOrSeniorEmployeesIds: string[] =
    formvalues.underageOrSeniorEmployees?.map(
      (empVal) => empVal.employeeId || ""
    ) || [];

  const selectedWhoPhysicalLaborIds: string[] =
    formvalues.employeesWhoRequirePhysicalLabor?.map(
      (empVal) => empVal.employeeId || ""
    ) || [];

  const selectedWhoUseBoatsBridgesIds: string[] =
    formvalues.employeesWhoUseBoatsBridges?.map((id) => id || "") || [""];

  const selectedPhysicalDisabilityIds: string[] =
    formvalues.employeesWhoHavePhysicalDisabilities?.map((id) => id || "") || [
      "",
    ];

  const selectedWhoWorkUndergroundIds: string[] =
    formvalues.employeesWhoWorkUnderground?.map(
      (empVal) => empVal.employeeId || ""
    ) || [];

  const selectedWhoWorkOnLaddersIds: string[] =
    formvalues.employeesWhoWorkOnLadders?.map(
      (empVal) => empVal.employeeId || ""
    ) || [];

  const selectedWhoPerformHeavyLiftingIds: string[] =
    formvalues.employeesWhoPerformHeavyLifting?.map(
      (empVal) => empVal.employeeId || ""
    ) || [];

  const selectedWhoMakeDeliveriesIds: string[] =
    formvalues.employeesWhoMakeDeliveries?.map(
      (empVal) => empVal.employeeId || ""
    ) || [];

  return (
    <div>
      <div className={styles.title}>
        {sectionsQuestions["employeesAttributes"].title}
      </div>
      <div className={styles.description}>
        {sectionsQuestions["employeesAttributes"].description}
      </div>
      <RadioInput
        name="travel"
        label={labelList.travel}
        message={messageList.travel}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasTravelEmployee === "Yes" && (
        <FieldArray
          name="employeesWhoTravel"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoTravel?.map((value, index) => (
                <div key={`employeesWhoTravel-${value}-${index}`}>
                  <EditCard title="Teammates who travel" showEditIcon={false}>
                    <SelectInput
                      name={`employeesWhoTravel[${index}.employeeId]`}
                      label="Select teammate"
                      options={employeeAndOwnerOptions}
                      hiddenOptions={selectedWhoTravelIds.filter((id) => {
                        return id !== value.employeeId;
                      })}
                      placeholder="Select an option"
                    />
                    <SelectInput
                      name={`employeesWhoTravel[${index}.travelFrequency]`}
                      options={travelFrequencyOptions}
                      label={labelList.employeesWhoTravel.travelFrequency}
                      placeholder="Select an option"
                    />
                    <RadioInput
                      name={`employeesWhoTravel[${index}.travelInternationally]`}
                      label={labelList.employeesWhoTravel.travelInternationally}
                      options={["Yes", "No"]}
                    />
                    {hasTravelInternationally(index) && (
                      <MultiSelectInput
                        name={`employeesWhoTravel[${index}.travelLocation]`}
                        values={hasCountryList[index]?.travelLocation}
                        options={countryOptions}
                        label={labelList.employeesWhoTravel.travelLocation}
                        placeholder="Select countries"
                        onChange={(selectedItems: any) => {
                          const selectedValues = selectedItems.map(
                            (option: any) => option.value as string
                          );
                          setFieldValue(
                            `employeesWhoTravel[${index}.travelLocation]`,
                            selectedValues.join(", ")
                          );
                        }}
                        fieldRefs={fieldRefs}
                      />
                    )}
                    <FooterButton
                      nextButtonText="Add more"
                      backButtonText="Delete"
                      handleBackClick={() => {
                        arrayHelpers.remove(index);
                        if (formvalues.employeesWhoTravel?.length === 1) {
                          arrayHelpers.push({
                            employeeId: "",
                            travelPurpose: "",
                            travelInternationally: "",
                            travelLocation: "",
                            travelFrequency: "",
                          });
                        }
                      }}
                      disabled={
                        formvalues.employeesWhoTravel?.length ===
                        employeeAndOwnerOptions?.length
                      }
                      handleNextClick={() =>
                        arrayHelpers.push({
                          employeeId: "",
                          travelPurpose: "",
                          travelInternationally: "",
                          travelLocation: "",
                          travelFrequency: "",
                        })
                      }
                    />
                  </EditCard>
                </div>
              ))}
            </div>
          )}
        />
      )}
      <RadioInput
        name="hardware"
        label={labelList.hardware}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasEmployeesWhoUseTools === "Yes" && (
        <FieldArray
          name="employeesWhoUseTools"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoUseTools?.map((value, index) => (
                <div key={`employeesWhoUseTools-${value}-${index}`}>
                  <EditCard
                    title="Teammates who use tools"
                    showEditIcon={false}
                  >
                    <SelectInput
                      name={`employeesWhoUseTools[${index}.employeeId]`}
                      label="Select teammate"
                      options={employeeAndOwnerOptions}
                      hiddenOptions={selectedWhoUseToolsIds.filter((id) => {
                        return id !== value.employeeId;
                      })}
                      placeholder="Select an option"
                    />
                    <TextArea
                      name={`employeesWhoUseTools[${index}.hardwareNames]`}
                      label={"Tools used"}
                    />
                    <FooterButton
                      nextButtonText="Add more"
                      backButtonText="Delete"
                      handleBackClick={() => {
                        arrayHelpers.remove(index);
                        if (formvalues.employeesWhoUseTools?.length === 1) {
                          arrayHelpers.push({
                            employeeId: "",
                            hardwareNames: "",
                          });
                        }
                      }}
                      disabled={
                        formvalues.employeesWhoUseTools?.length ===
                        employeeAndOwnerOptions?.length
                      }
                      handleNextClick={() =>
                        arrayHelpers.push({
                          employeeId: "",
                          hardwareNames: "",
                        })
                      }
                    />
                  </EditCard>
                </div>
              ))}
            </div>
          )}
        />
      )}
      <RadioInput
        name="employeesPhysicalLabor"
        label={labelList.employeesPhysicalLabor}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasEmployeesPhysicalLabor === "Yes" && (
        <FieldArray
          name="employeesWhoRequirePhysicalLabor"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoRequirePhysicalLabor?.map(
                (value, index) => (
                  <div
                    key={`employeesWhoRequirePhysicalLabor-${value}-${index}`}
                  >
                    <EditCard
                      title="Teammates who require physical labor"
                      showEditIcon={false}
                    >
                      <SelectInput
                        name={`employeesWhoRequirePhysicalLabor[${index}.employeeId]`}
                        label="Select teammate"
                        options={employeeAndOwnerOptions}
                        hiddenOptions={selectedWhoPhysicalLaborIds.filter(
                          (id) => {
                            return id !== value.employeeId;
                          }
                        )}
                        placeholder="Select an option"
                      />
                      <SelectInput
                        name={`employeesWhoRequirePhysicalLabor[${index}].percentPhysicalLabor`}
                        options={percentageOptions}
                        label={
                          labelList.employeesWhoRequirePhysicalLabor
                            .percentPhysicalLabor
                        }
                        placeholder="Select an option"
                      />
                      <FooterButton
                        nextButtonText="Add more"
                        backButtonText="Delete"
                        handleBackClick={() => {
                          arrayHelpers.remove(index);
                          if (
                            formvalues.employeesWhoRequirePhysicalLabor
                              ?.length === 1
                          ) {
                            arrayHelpers.push({
                              employeeId: "",
                              percentPhysicalLabor: "",
                            });
                          }
                        }}
                        disabled={
                          formvalues.employeesWhoRequirePhysicalLabor
                            ?.length === employeeAndOwnerOptions?.length
                        }
                        handleNextClick={() =>
                          arrayHelpers.push({
                            employeeId: "",
                            percentPhysicalLabor: "",
                          })
                        }
                      />
                    </EditCard>
                  </div>
                )
              )}
            </div>
          )}
        />
      )}
      <RadioInput
        name="hasContractors"
        label={labelList.hasContractors}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasContractors === "Yes" && (
        <>
          <TextInput
            name="employmentDuration"
            label={labelList.employmentDuration}
            fieldRefs={fieldRefs}
          />
          <SelectInput
            name="numberContractors"
            options={contractorNumberOption}
            label={labelList.numberContractors}
            placeholder="Select an option"
            fieldRefs={fieldRefs}
          />
          <SelectInput
            name="percentContractors"
            options={percentageOptions}
            label={labelList.percentContractors}
            placeholder="Select an option"
            fieldRefs={fieldRefs}
          />
          <TextArea
            name="contractorWorkType"
            label={labelList.contractorWorkType}
            fieldRefs={fieldRefs}
          />
          <RadioInput
            name="contractorsHaveWorkComp"
            label={labelList.contractorsHaveWorkComp}
            options={["Yes", "No"]}
            fieldRefs={fieldRefs}
          />
          {doContractorsHaveWorkComp === "Yes" && (
            <RadioInput
              name="contractorsHaveWorkersCompCois"
              label={labelList.contractorsHaveWorkersCompCois}
              options={["Yes", "No"]}
              fieldRefs={fieldRefs}
            />
          )}
        </>
      )}
      <TextInput
        name="numberTemp"
        type="string"
        label={labelList.numberTemp}
        fieldRefs={fieldRefs}
      />
      <RadioInput
        name="boatsBridges"
        label={labelList.boatsBridges}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasBoatsBridgesEmployees === "Yes" && (
        <MultiSelectInput
          className="multiselect-container"
          name="employeesWhoUseBoatsBridges"
          values={hasEmployeesBoatsBridges}
          options={employeeAndOwnerOptions}
          label="Select teammates"
          placeholder="Select teammates"
          onChange={(selectedItems: any) => {
            const selectedValues = selectedItems.map(
              (option: any) => option.value as string
            );
            setFieldValue("employeesWhoUseBoatsBridges", selectedValues);
          }}
          hiddenOptions={selectedWhoUseBoatsBridgesIds.filter((id) => {
            return formvalues?.employeesWhoUseBoatsBridges?.map(
              (selectedId) => {
                return id !== selectedId;
              }
            );
          })}
          fieldRefs={fieldRefs}
        />
      )}
      <RadioInput
        name="employeesWorkUnderground"
        label={labelList.employeesWorkUnderground}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasEmployeesWorkUnderground === "Yes" && (
        <FieldArray
          name="employeesWhoWorkUnderground"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoWorkUnderground?.map((value, index) => (
                <div key={`employeesWhoWorkUnderground-${value}-${index}`}>
                  <EditCard
                    title="Teammates who work underground"
                    showEditIcon={false}
                  >
                    <SelectInput
                      name={`employeesWhoWorkUnderground[${index}.employeeId]`}
                      label="Select teammate"
                      options={employeeAndOwnerOptions}
                      hiddenOptions={selectedWhoWorkUndergroundIds.filter(
                        (id) => {
                          return id !== value.employeeId;
                        }
                      )}
                      placeholder="Select an option"
                    />
                    <TextArea
                      name={`employeesWhoWorkUnderground[${index}.undergroundDescription]`}
                      label={
                        labelList.employeesWhoWorkUnderground
                          .undergroundDescription
                      }
                    />
                    <FooterButton
                      nextButtonText="Add more"
                      backButtonText="Delete"
                      handleBackClick={() => {
                        arrayHelpers.remove(index);
                        if (
                          formvalues.employeesWhoWorkUnderground?.length === 1
                        ) {
                          arrayHelpers.push({
                            employeeId: "",
                            undergroundDescription: "",
                          });
                        }
                      }}
                      disabled={
                        formvalues.employeesWhoWorkUnderground?.length ===
                        employeeAndOwnerOptions?.length
                      }
                      handleNextClick={() =>
                        arrayHelpers.push({
                          employeeId: "",
                          undergroundDescription: "",
                        })
                      }
                    />
                  </EditCard>
                </div>
              ))}
            </div>
          )}
        />
      )}
      <RadioInput
        name="employeesWorkOnLadders"
        label={labelList.employeesWorkOnLadders}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasEmployeesWorkOnLadders === "Yes" && (
        <FieldArray
          name="employeesWhoWorkOnLadders"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoWorkOnLadders?.map((value, index) => (
                <div key={`employeesWhoWorkOnLadders-${value}-${index}`}>
                  <EditCard
                    title="Teammates who work on ladders"
                    showEditIcon={false}
                  >
                    <SelectInput
                      name={`employeesWhoWorkOnLadders[${index}.employeeId]`}
                      label="Select teammate"
                      options={employeeAndOwnerOptions}
                      hiddenOptions={selectedWhoWorkOnLaddersIds.filter(
                        (id) => {
                          return id !== value.employeeId;
                        }
                      )}
                      placeholder="Select an option"
                    />
                    <TextArea
                      name={`employeesWhoWorkOnLadders[${index}.laddersDescription]`}
                      label={
                        labelList.employeesWhoWorkOnLadders.laddersDescription
                      }
                    />
                    <FooterButton
                      nextButtonText="Add more"
                      backButtonText="Delete"
                      handleBackClick={() => {
                        arrayHelpers.remove(index);
                        if (
                          formvalues.employeesWhoWorkOnLadders?.length === 1
                        ) {
                          arrayHelpers.push({
                            employeeId: "",
                            laddersDescription: "",
                          });
                        }
                      }}
                      disabled={
                        formvalues.employeesWhoWorkOnLadders?.length ===
                        employeeAndOwnerOptions?.length
                      }
                      handleNextClick={() =>
                        arrayHelpers.push({
                          employeeId: "",
                          laddersDescription: "",
                        })
                      }
                    />
                  </EditCard>
                </div>
              ))}
            </div>
          )}
        />
      )}
      <RadioInput
        name="employeePerformHeavyLifting"
        label={labelList.employeePerformHeavyLifting}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasEmployeePerformHeavyLifting === "Yes" && (
        <FieldArray
          name="employeesWhoPerformHeavyLifting"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoPerformHeavyLifting?.map(
                (value, index) => (
                  <div
                    key={`employeesWhoPerformHeavyLifting-${value}-${index}`}
                  >
                    <EditCard
                      title="Teammates who perform heavy lifting"
                      showEditIcon={false}
                    >
                      <SelectInput
                        name={`employeesWhoPerformHeavyLifting[${index}.employeeId]`}
                        label="Select teammate"
                        options={employeeAndOwnerOptions}
                        hiddenOptions={selectedWhoPerformHeavyLiftingIds.filter(
                          (id) => {
                            return id !== value.employeeId;
                          }
                        )}
                        placeholder="Select an option"
                      />
                      <TextArea
                        name={`employeesWhoPerformHeavyLifting[${index}.maximumWeightLifted]`}
                        label={
                          labelList.employeesWhoPerformHeavyLifting
                            .maximumWeightLifted
                        }
                        message={messageList.maximumWeightLifted}
                      />
                      <FooterButton
                        nextButtonText="Add more"
                        backButtonText="Delete"
                        handleBackClick={() => {
                          arrayHelpers.remove(index);
                          if (
                            formvalues.employeesWhoPerformHeavyLifting
                              ?.length === 1
                          ) {
                            arrayHelpers.push({
                              employeeId: "",
                              maximumWeightLifted: "",
                            });
                          }
                        }}
                        disabled={
                          formvalues.employeesWhoPerformHeavyLifting?.length ===
                          employeeAndOwnerOptions?.length
                        }
                        handleNextClick={() =>
                          arrayHelpers.push({
                            employeeId: "",
                            maximumWeightLifted: "",
                          })
                        }
                      />
                    </EditCard>
                  </div>
                )
              )}
            </div>
          )}
        />
      )}
      <RadioInput
        name="deliveries"
        label={labelList.deliveries}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasDeliveries === "Yes" && (
        <FieldArray
          name="employeesWhoMakeDeliveries"
          render={(arrayHelpers) => (
            <div>
              {formvalues.employeesWhoMakeDeliveries?.map((value, index) => (
                <div key={`employeesWhoMakeDeliveries-${value}-${index}`}>
                  <EditCard
                    title="Teammates who make deliveries"
                    showEditIcon={false}
                  >
                    <SelectInput
                      name={`employeesWhoMakeDeliveries[${index}.employeeId]`}
                      label="Select teammate"
                      options={employeeAndOwnerOptions}
                      hiddenOptions={selectedWhoMakeDeliveriesIds.filter(
                        (id) => {
                          return id !== value.employeeId;
                        }
                      )}
                      placeholder="Select an option"
                    />
                    <TextArea
                      name={`employeesWhoMakeDeliveries[${index}.deliveryTransportationType]`}
                      label={
                        labelList.employeesWhoMakeDeliveries
                          .deliveryTransportationType
                      }
                    />
                    <TextArea
                      name={`employeesWhoMakeDeliveries[${index}.deliveryDistance]`}
                      label={
                        labelList.employeesWhoMakeDeliveries.deliveryDistance
                      }
                    />
                    <TextArea
                      name={`employeesWhoMakeDeliveries[${index}.deliveryFrequency]`}
                      label={
                        labelList.employeesWhoMakeDeliveries.deliveryFrequency
                      }
                    />
                    <FooterButton
                      nextButtonText="Add more"
                      backButtonText="Delete"
                      handleBackClick={() => {
                        arrayHelpers.remove(index);
                        if (
                          formvalues.employeesWhoMakeDeliveries?.length === 1
                        ) {
                          arrayHelpers.push({
                            employeeId: "",
                            deliveryTransportationType: "",
                            deliveryDistance: "",
                            deliveryFrequency: "",
                          });
                        }
                      }}
                      disabled={
                        formvalues.employeesWhoMakeDeliveries?.length ===
                        employeeAndOwnerOptions?.length
                      }
                      handleNextClick={() =>
                        arrayHelpers.push({
                          employeeId: "",
                          deliveryTransportationType: "",
                          deliveryDistance: "",
                          deliveryFrequency: "",
                        })
                      }
                    />
                  </EditCard>
                </div>
              ))}
            </div>
          )}
        />
      )}
      <RadioInput
        name="physicalDisability"
        label={labelList.physicalDisability}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasPhysicalDisabilityEmployees === "Yes" && (
        <MultiSelectInput
          name="employeesWhoHavePhysicalDisabilities"
          values={hasEmployeesPhysicalDisability}
          options={employeeAndOwnerOptions}
          label="Select teammates"
          placeholder="Select teammates"
          onChange={(selectedItems: any) => {
            const selectedValues = selectedItems.map(
              (option: any) => option.value as string
            );
            setFieldValue(
              "employeesWhoHavePhysicalDisabilities",
              selectedValues
            );
          }}
          hiddenOptions={selectedPhysicalDisabilityIds.filter((id) => {
            return formvalues?.employeesWhoHavePhysicalDisabilities?.map(
              (selectedId) => {
                return id !== selectedId;
              }
            );
          })}
          fieldRefs={fieldRefs}
        />
      )}
      <RadioInput
        name="underageOrSenior"
        label={labelList.underageOrSenior}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {hasUnderageOrSeniorEmployees === "Yes" && (
        <FieldArray
          name="underageOrSeniorEmployees"
          render={(arrayHelpers) => (
            <div>
              {formvalues.underageOrSeniorEmployees?.map((value, index) => (
                <div key={`underageOrSeniorEmployees-${value}-${index}`}>
                  <EditCard
                    title="Employees under 16 or 60+"
                    showEditIcon={false}
                  >
                    <SelectInput
                      name={`underageOrSeniorEmployees[${index}.employeeId]`}
                      label="Select teammate"
                      options={employeeAndOwnerOptions}
                      hiddenOptions={selectedUnderageOrSeniorEmployeesIds.filter(
                        (id) => {
                          return id !== value.employeeId;
                        }
                      )}
                      placeholder="Select an option"
                    />
                    <DateInput
                      name={`underageOrSeniorEmployees[${index}.dateOfBirth]`}
                      label={"Date of birth"}
                    />
                    <TextInput
                      name={`underageOrSeniorEmployees[${index}.jobTitle]`}
                      label={"Job title"}
                    />
                    <FooterButton
                      nextButtonText="Add more"
                      backButtonText="Delete"
                      handleBackClick={() => {
                        arrayHelpers.remove(index);
                        if (
                          formvalues.underageOrSeniorEmployees?.length === 1
                        ) {
                          arrayHelpers.push({
                            dateOfBirth: "",
                            employeeId: "",
                            jobTitle: "",
                          });
                        }
                      }}
                      disabled={
                        formvalues.underageOrSeniorEmployees?.length ===
                        employeeAndOwnerOptions?.length
                      }
                      handleNextClick={() =>
                        arrayHelpers.push({
                          dateOfBirth: "",
                          employeeId: "",
                          jobTitle: "",
                        })
                      }
                    />
                  </EditCard>
                </div>
              ))}
            </div>
          )}
        />
      )}
    </div>
  );
};

export default EmployeeAttributesComponent;
